/**
 * Copyright © Amazon.com and Affiliates: This deliverable is considered Developed Content as defined in the AWS Service Terms 
 * and the SOW between the parties dated 10/31/2024.
 */
import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, tap, shareReplay } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class SettingsService {
  private API_URL: string;
  private userSettings$: Observable<any> | null = null;

  constructor(private http: HttpClient, @Inject('API_URL') apiUrl: string) {
    this.API_URL = apiUrl;
  }

  /**
   * Fetches user settings from the API with caching
   * Uses shareReplay to ensure only one HTTP request is made and cached
   * @returns Observable of the settings response
   */
  getUserSettings(): Observable<any> {
    // If we already have a cached observable, return it
    if (this.userSettings$) {
      return this.userSettings$;
    }

    const token = localStorage.getItem('token');
    const idToken = localStorage.getItem('id_token');
    
    if (!token || !idToken) {
      console.error('SettingsService - Missing authentication tokens');
      return throwError(() => new Error('Missing authentication tokens'));
    }
    
    const headers = new HttpHeaders()
      .set('Authorization', `${token}`)
      .set('id_token', `${idToken}`);
    
    // Create and cache the observable with shareReplay
    this.userSettings$ = this.http.get<any>(`${this.API_URL}/user/settings`, { headers }).pipe(
      shareReplay(1), // Cache the last emission and replay it to new subscribers
      catchError(this.handleError)
    );

    return this.userSettings$;
  }

  /**
   * Clears the cached user settings
   * Call this when you need to force a fresh fetch (e.g., after settings update)
   */
  clearCache(): void {
    this.userSettings$ = null;
  }

  /**
   * Handles HTTP errors
   * @param error The HTTP error response
   * @returns An observable with a user-facing error message
   */
  private handleError(error: HttpErrorResponse) {
    if (error.status === 0) {
      // A client-side or network error occurred
      console.error('An error occurred:', error.error);
    } else {
      // The backend returned an unsuccessful response code
      console.error(
        `Backend returned code ${error.status}, body was: ${JSON.stringify(error.error)}`
      );
    }
    // Return an observable with a user-facing error message
    return throwError(() => new Error(`Error: ${error.error?.message}`));
  }
}
